import React, { useState, useRef, useEffect } from 'react';

import {
  SelectedOption,
  ArrowIcon,
  SelectedOptionLabel,
  Container,
  Placeholder
} from './SelectInput.styled';
import ArrowDown from 'assets/icons/arrow_down.svg?react';
import ArrowUp from 'assets/icons/arrow_up.svg?react';
import { DropdownMenu } from 'components/v2/Inputs/DropdownMenu/DropdownMenu';

import { Option, Props } from './SelectInput.types';
import { getInputStates } from '../../utils';

export const SelectInput = ({
  id,
  labelId,
  dropdownId,
  status = 'default',
  searchPlaceholder,
  options,
  searchable,
  selected,
  placeholder,
  onChange,
  wrapSelectedOptionText,
  positionStatic,
  maxMenuHeight,
  multiSelect
}: Props): React.JSX.Element => {
  const { isDisabled } = getInputStates(status);
  const [isDropDownOpened, setIsDropDownOpened] = useState<boolean>(false);
  const dropDownMenuRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (Array.isArray(selected)) {
      setSelectedOptions(selected);
    } else {
      setSelectedOptions(selected ? [selected] : []);
    }
  }, [selected]);

  const clickOutside = (e: Event) => {
    if (
      !dropDownMenuRef.current?.contains(e.target as Node) &&
      !labelRef.current?.contains(e.target as Node)
    ) {
      setIsDropDownOpened(false);
    }
  };

  const toggle = () => {
    if (isDisabled) return;

    setIsDropDownOpened(!isDropDownOpened);
  };

  const select = (option: Option) => {
    if (!multiSelect) setIsDropDownOpened(false);
    onChange(option);
  };

  const getSelectedTitle = () => {
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return selectedOptions.map(option => option.label).join(', ');
  };

  useEffect(() => {
    document.addEventListener('mousedown', clickOutside);

    return () => document.removeEventListener('mousedown', clickOutside);
  }, [isDropDownOpened]);

  return (
    <Container>
      <SelectedOption
        id={id}
        $isFilled={Boolean(selectedOptions)}
        onClick={toggle}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          } else if (e.key === 'Escape' && isDropDownOpened) {
            setIsDropDownOpened(false);
          }
        }}
        tabIndex={isDisabled ? -1 : 0}
        $isDropDownOpened={isDropDownOpened}
        role="combobox"
        aria-labelledby={labelId}
        aria-expanded={isDropDownOpened}
        aria-controls={dropdownId}
        aria-autocomplete="none"
        $status={status}
      >
        <SelectedOptionLabel
          ref={labelRef}
          title={getSelectedTitle()}
          $wrapSelectedOptionText={wrapSelectedOptionText}
        >
          {getSelectedTitle() || <Placeholder $status={status}>{placeholder}</Placeholder>}
        </SelectedOptionLabel>
        <ArrowIcon>{isDropDownOpened ? <ArrowUp /> : <ArrowDown />}</ArrowIcon>
      </SelectedOption>

      {isDropDownOpened && (
        <DropdownMenu
          id={dropdownId}
          ref={dropDownMenuRef}
          options={options}
          selected={selectedOptions}
          onSelect={select}
          onClose={() => setIsDropDownOpened(false)}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          positionStatic={positionStatic}
          maxMenuHeight={maxMenuHeight}
        />
      )}
    </Container>
  );
};
